import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { posApi, authApi } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { useSyncStore } from '@/store/syncStore';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import SyncModeBanner from '@/components/pos/SyncModeBanner';
import { ShoppingCart, Users, Receipt, ArrowRight, Menu, CheckCircle, Percent } from 'lucide-react';
import PakRupeeIcon from '@/components/common/PakRupeeIcon';
import { formatMoney } from '@/utils/currency';
import { Link } from 'react-router-dom';
import { isCompanyAdmin, isManager } from '@/types';
import { isLocalFirstMode, localDailySummary } from '@/offline';
import PendingShiftApprovals from '@/components/dashboard/PendingShiftApprovals';
import { reportsApi } from '@/services/api';
import { isUk } from '@/data/locales';

function translateSubscriptionLabel(value: string | undefined, t: (key: string, opts?: { defaultValue?: string }) => string): string {
  if (!value) return '';
  const key = value.toLowerCase().replace(/\s+/g, '_');
  return t(`subscription.${key}`, { defaultValue: value });
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user, company, role } = useAuthStore();
  const { toggleSidebar } = useUIStore();
  const posSyncMode = useSyncStore((s) => s.posSyncMode);
  const { data: summary } = useQuery({
    queryKey: ['dailySummary', posSyncMode],
    queryFn: async () => {
      if (isLocalFirstMode()) {
        return localDailySummary();
      }
      const res = await posApi.getDailySummary();
      return res.data.message;
    },
  });
  const { data: profile } = useQuery({
    queryKey: ['profile'], queryFn: async () => {
      const res = await authApi.getCompanyProfile(); return res.data.message;
    }
  });

  const showUkVat = isUk(profile?.country) && (isCompanyAdmin(role) || isManager(role));
  const monthStart = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  })();
  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  const { data: vatMonth } = useQuery({
    queryKey: ['vatReturnSummary', 'dashboard', monthStart, todayStr],
    queryFn: async () => {
      const res = await reportsApi.getVatReturnSummary({ from_date: monthStart, to_date: todayStr });
      return res as {
        summary: {
          output_vat: number;
          vat_reversed: number;
          net_vat_due?: number;
          net_output_vat: number;
          standard_rated_sales: number;
          reduced_rated_sales?: number;
          zero_rated_sales: number;
        };
        company_info?: { currency?: string };
      };
    },
    enabled: showUkVat && !isLocalFirstMode(),
  });

  const stats = [
    { key: 'totalSales', label: t('dashboard.totalSales'), value: formatMoney(summary?.total_sales || 0), icon: PakRupeeIcon, color: 'bg-emerald-500' },
    { key: 'invoices', label: t('dashboard.invoices'), value: summary?.invoice_count || 0, icon: Receipt, color: 'bg-blue-500' },
    { key: 'productSales', label: t('dashboard.productSales'), value: formatMoney(summary?.item_sales || 0), icon: ShoppingCart, color: 'bg-amber-500' },
    { key: 'serviceSales', label: t('dashboard.serviceSales'), value: formatMoney(summary?.service_sales || 0), icon: Users, color: 'bg-purple-500' },
  ];

  const quickActions = [
    { key: 'newSale', label: t('dashboard.newSale'), desc: t('dashboard.newSaleDesc'), to: '/', color: 'bg-primary-600' },
    { key: 'products', label: t('nav.products'), desc: t('dashboard.productsDesc'), to: '/items', color: 'bg-slate-700' },
    { key: 'customers', label: t('nav.customers'), desc: t('dashboard.customersDesc'), to: '/customers', color: 'bg-slate-700' },
    { key: 'analytics', label: t('nav.analytics'), desc: t('dashboard.analyticsDesc'), to: '/analytics', color: 'bg-slate-700' },
    { key: 'reports', label: t('nav.reports'), desc: t('dashboard.reportsDesc'), to: '/reports/sales/register', color: 'bg-slate-700' },
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <SyncModeBanner />
        <TrialBanner />
        {isLocalFirstMode() && (
          <p className="mx-6 mt-2 text-xs text-slate-500">{t('dashboard.localDataNote')}</p>
        )}
        <div className="p-6">
          <div className="mb-6 flex items-start sm:items-center justify-between gap-3">
            <div className="flex items-start sm:items-center gap-3">
              <button onClick={toggleSidebar} className="lg:hidden p-2 -ml-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg shrink-0">
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
                <p className="text-gray-500 text-sm">
                  {t('common.welcomeBack', { name: user?.full_name, company: company?.name })}
                  {' · '}
                  {t('dashboard.periodToday', { defaultValue: 'Today' })}
                </p>
              </div>
            </div>

            {isCompanyAdmin(role) && profile?.status === 'Active' && !profile?.is_nearing_expiration && (
              <Link to="/support" className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-full transition-colors border border-emerald-100">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">{t('common.subscribed')}</span>
              </Link>
            )}
          </div>

          {(isCompanyAdmin(role) || role === 'Manager') && (
            <PendingShiftApprovals />
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {stats.map((s) => (
              <div key={s.key} className="card p-5">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 ${s.color} rounded-lg flex items-center justify-center`}>
                    <s.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{s.label}</p>
                    <p className="text-xl font-bold text-gray-900">{s.value}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {showUkVat && vatMonth?.summary && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('dashboard.ukVatThisMonth', { defaultValue: 'UK VAT (this month)' })}
                </h2>
                <Link
                  to="/reports/analysis/vat-summary"
                  className="text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  {t('dashboard.viewVatReturn', { defaultValue: 'View VAT Return Summary' })}
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="card p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 bg-emerald-500 rounded-lg flex items-center justify-center">
                      <Percent className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-sm text-gray-500">Output VAT</p>
                  </div>
                  <p className="text-xl font-bold text-gray-900">
                    {formatMoney(vatMonth.summary.output_vat || 0, {
                      currency: vatMonth.company_info?.currency || 'GBP',
                    })}
                  </p>
                </div>
                <div className="card p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 bg-rose-500 rounded-lg flex items-center justify-center">
                      <Percent className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-sm text-gray-500">VAT on returns</p>
                  </div>
                  <p className="text-xl font-bold text-gray-900">
                    {formatMoney(vatMonth.summary.vat_reversed || 0, {
                      currency: vatMonth.company_info?.currency || 'GBP',
                    })}
                  </p>
                </div>
                <div className="card p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 bg-indigo-500 rounded-lg flex items-center justify-center">
                      <Percent className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-sm text-gray-500">Net VAT due</p>
                  </div>
                  <p className="text-xl font-bold text-indigo-700">
                    {formatMoney(
                      vatMonth.summary.net_vat_due ?? vatMonth.summary.net_output_vat ?? 0,
                      { currency: vatMonth.company_info?.currency || 'GBP' },
                    )}
                  </p>
                </div>
                <div className="card p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 bg-slate-600 rounded-lg flex items-center justify-center">
                      <Receipt className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-sm text-gray-500">Sales ex VAT (S / R / Z)</p>
                  </div>
                  <p className="text-sm font-medium text-gray-900 tabular-nums">
                    {formatMoney(vatMonth.summary.standard_rated_sales || 0, {
                      currency: vatMonth.company_info?.currency || 'GBP',
                    })}
                    {' / '}
                    {formatMoney(vatMonth.summary.reduced_rated_sales || 0, {
                      currency: vatMonth.company_info?.currency || 'GBP',
                    })}
                    {' / '}
                    {formatMoney(vatMonth.summary.zero_rated_sales || 0, {
                      currency: vatMonth.company_info?.currency || 'GBP',
                    })}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-2 xs:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {quickActions.map((a) => (
              <Link key={a.key} to={a.to} className={`${a.color} rounded-xl p-5 text-white hover:opacity-90 transition-opacity`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{a.label}</p>
                    <p className="text-sm opacity-80">{a.desc}</p>
                  </div>
                  <ArrowRight className="w-5 h-5" />
                </div>
              </Link>
            ))}
          </div>

          {/* Company Info */}
          {profile && (
            <div className="card p-5">
              <h3 className="font-semibold text-gray-900 mb-3">{t('dashboard.companyInfo')}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><p className="text-gray-500">{t('common.status')}</p><p className="font-medium">{translateSubscriptionLabel(profile.status, t)}</p></div>
                <div><p className="text-gray-500">{t('common.package')}</p><p className="font-medium">{translateSubscriptionLabel(profile.package?.name, t) || t('common.none')}</p></div>
                {profile.status !== 'Active' && (
                  <div><p className="text-gray-500">{t('dashboard.trialEnds')}</p><p className="font-medium">{profile.trial_end || t('common.na')}</p></div>
                )}
                {profile.subscription_details && (
                  <>
                    <div><p className="text-gray-500">{t('dashboard.subStart')}</p><p className="font-medium">{profile.subscription_details.start_date}</p></div>
                    <div><p className="text-gray-500">{t('dashboard.subEnd')}</p><p className="font-medium">{profile.subscription_details.end_date}</p></div>
                  </>
                )}
                <div><p className="text-gray-500">{t('common.owner')}</p><p className="font-medium">{profile.owner_name}</p></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
